import React, { useState } from "react";
import { CalendarDays, X } from "lucide-react";
import { format } from "date-fns";
import { es, enUS, it } from "date-fns/locale";
import { useI18n } from "../i18n/I18nContext";
import { Calendar } from "./ui/calendar";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const LOCALES = { es, en: enUS, it };

export default function DateRangePicker({ range, onChange }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const locale = LOCALES[lang] || es;

  const applyPreset = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    onChange({ from, to });
    setOpen(false);
  };

  const label = range?.from && range?.to
    ? `${format(range.from, "d MMM yyyy", { locale })} — ${format(range.to, "d MMM yyyy", { locale })}`
    : t("apanel.dateAll");

  const presets = [
    { key: "today", days: 0 }, { key: "d7", days: 7 }, { key: "d30", days: 30 }, { key: "d90", days: 90 },
  ];

  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button data-testid="date-range-trigger"
            className="flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-700 hover:border-[#008080] transition-colors">
            <CalendarDays className="h-4 w-4 text-[#008080]" />
            {label}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0" data-testid="date-range-popover">
          <div className="flex flex-wrap gap-1.5 p-3 border-b border-slate-100">
            {presets.map((p) => (
              <button key={p.key} data-testid={`date-preset-${p.key}`} onClick={() => applyPreset(p.days)}
                className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-teal-50 hover:text-[#008080] transition-colors">
                {t(`apanel.preset_${p.key}`)}
              </button>
            ))}
          </div>
          <Calendar mode="range" numberOfMonths={2} locale={locale} defaultMonth={range?.from}
            selected={range} onSelect={(r) => { onChange(r || {}); if (r?.from && r?.to) setOpen(false); }} />
        </PopoverContent>
      </Popover>
      {range?.from && (
        <button data-testid="date-range-clear" onClick={() => onChange({})}
          className="h-10 w-10 grid place-items-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-rose-500 transition-colors">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
